import React, { useState, useEffect, useRef } from 'react';
import './MapsTestPage.css';

const MapsTestPage = () => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [searchRadius, setSearchRadius] = useState(1000);
  const [placeType, setPlaceType] = useState('restaurant');
  const [mapType, setMapType] = useState('roadmap');
  const [markers, setMarkers] = useState([]);

  // Initialize Google Maps (without Places library)
  useEffect(() => {
    const initMap = () => {
      // Check if Google Maps API is fully loaded
      if (!window.google || !window.google.maps || !window.google.maps.Map) {
        setError('Google Maps API not fully loaded');
        if (window.adminDebug) {
          window.adminDebug.addLog('Google Maps API not fully loaded', 'error', {
            hasGoogle: !!window.google,
            hasMaps: !!(window.google && window.google.maps),
            hasMap: !!(window.google && window.google.maps && window.google.maps.Map)
          });
        }
        return;
      }

      // Check if map container exists
      if (!mapRef.current) {
        setError('Map container not found');
        return;
      }

      try {
        // Default location (San Francisco)
        const defaultLocation = { lat: 37.7749, lng: -122.4194 };
        
        const mapInstance = new window.google.maps.Map(mapRef.current, {
          center: defaultLocation,
          zoom: 15,
          mapTypeId: mapType,
          mapTypeControl: false,
          streetViewControl: true,
          fullscreenControl: true,
          gestureHandling: 'greedy',
          styles: [
            {
              featureType: "poi.business",
              elementType: "labels",
              stylers: [{ visibility: "off" }]
            },
            {
              featureType: "poi.government",
              elementType: "labels", 
              stylers: [{ visibility: "off" }]
            },
            {
              featureType: "poi.medical",
              elementType: "labels",
              stylers: [{ visibility: "off" }]
            },
            {
              featureType: "poi.school",
              elementType: "labels",
              stylers: [{ visibility: "off" }]
            }
          ]
        });

        setMap(mapInstance);
        setError(null); // Clear any previous errors
        setIsInitializing(false); // Mark initialization as complete

        if (window.adminDebug) {
          window.adminDebug.addLog('Google Maps initialized successfully', 'info', {
            center: defaultLocation,
            zoom: 15
          });
        }

        // Try to get user's location
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const userPos = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              };
              setUserLocation(userPos);
              mapInstance.setCenter(userPos);
              
              // Add user location marker
              new window.google.maps.Marker({
                position: userPos,
                map: mapInstance,
                title: 'Your Location',
                icon: {
                  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <!-- Outer pulsing ring -->
                      <circle cx="20" cy="20" r="18" fill="#3B82F6" fill-opacity="0.2" stroke="#3B82F6" stroke-width="2" stroke-opacity="0.4">
                        <animate attributeName="r" values="12;18;12" dur="2s" repeatCount="indefinite"/>
                        <animate attributeName="fill-opacity" values="0.4;0.1;0.4" dur="2s" repeatCount="indefinite"/>
                      </circle>
                      <!-- Middle ring -->
                      <circle cx="20" cy="20" r="12" fill="#3B82F6" fill-opacity="0.3" stroke="#FFFFFF" stroke-width="2"/>
                      <!-- Inner dot -->
                      <circle cx="20" cy="20" r="6" fill="#3B82F6" stroke="#FFFFFF" stroke-width="2"/>
                      <!-- Center dot -->
                      <circle cx="20" cy="20" r="3" fill="#FFFFFF"/>
                    </svg>
                  `),
                  scaledSize: new window.google.maps.Size(40, 40),
                  anchor: new window.google.maps.Point(20, 20), // Center the icon
                },
                zIndex: 1000, // Ensure it appears above other markers
              });

              // Log to debug panel
              if (window.adminDebug) {
                window.adminDebug.addLog('User location obtained', 'info', {
                  latitude: userPos.lat,
                  longitude: userPos.lng
                });
              }
            },
            (error) => {
              console.warn('Geolocation error:', error);
              if (window.adminDebug) {
                window.adminDebug.addLog('Geolocation failed', 'warning', {
                  error: error.message
                });
              }
            }
          );
        }
      } catch (error) {
        setError(`Failed to initialize map: ${error.message}`);
        setIsInitializing(false);
        if (window.adminDebug) {
          window.adminDebug.addLog('Map initialization failed', 'error', {
            error: error.message,
            stack: error.stack
          });
        }
      }
    };

    // Check if API key is configured
    if (!process.env.REACT_APP_GOOGLE_API_KEY) {
      setError('Google API key not configured');
      setIsInitializing(false);
      if (window.adminDebug) {
        window.adminDebug.addLog('Google API key missing', 'error');
      }
      return;
    }

    // Load Google Maps API if not already loaded (without places library)
    if (!window.google || !window.google.maps) {
      // Check if script is already being loaded
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        // Script is already loading, wait for it
        existingScript.addEventListener('load', initMap);
        existingScript.addEventListener('error', () => {
          setError('Failed to load Google Maps API');
          if (window.adminDebug) {
            window.adminDebug.addLog('Google Maps script load failed', 'error');
          }
        });
        return;
      }

      const script = document.createElement('script');
      // Note: Removed &libraries=places since we're using the new Places API
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;
      
      // Create a global callback function
      window.initGoogleMaps = () => {
        if (window.adminDebug) {
          window.adminDebug.addLog('Google Maps API loaded', 'info');
        }
        initMap();
        // Clean up the global callback
        delete window.initGoogleMaps;
      };
      
      script.onerror = () => {
        setError('Failed to load Google Maps API');
        if (window.adminDebug) {
          window.adminDebug.addLog('Google Maps script load failed', 'error');
        }
        delete window.initGoogleMaps;
      };
      
      document.head.appendChild(script);
      
      if (window.adminDebug) {
        window.adminDebug.addLog('Loading Google Maps API', 'info', {
          apiKey: process.env.REACT_APP_GOOGLE_API_KEY ? 'Configured' : 'Missing'
        });
      }
    } else {
      // API is already loaded
      initMap();
    }

    // Cleanup function
    return () => {
      if (window.initGoogleMaps) {
        delete window.initGoogleMaps;
      }
    };
  }, []);

  // Update map type when mapType state changes
  useEffect(() => {
    if (map) {
      map.setMapTypeId(mapType);
    }
  }, [map, mapType]);

  // Search for nearby places using Places API (New)
  const searchNearbyPlaces = async () => {
    if (!map) {
      setError('Maps not initialized');
      return;
    }

    setLoading(true);
    setError(null);
    setNearbyPlaces([]);

    // Clear existing markers
    markers.forEach(marker => marker.setMap(null));
    setMarkers([]);

    // Get location coordinates - handle both userLocation object and map.getCenter() LatLng object
    let latitude, longitude;
    if (userLocation) {
      latitude = userLocation.lat;
      longitude = userLocation.lng;
    } else {
      const center = map.getCenter();
      latitude = center.lat();
      longitude = center.lng();
    }
    
    // Convert place type to includedTypes format for new API
    const getIncludedTypes = (type) => {
      const typeMapping = {
        'restaurant': ['restaurant'],
        'cafe': ['cafe'],
        'bar': ['bar'],
        'food': ['restaurant', 'cafe', 'bakery', 'meal_takeaway'],
        'meal_takeaway': ['meal_takeaway'],
        'bakery': ['bakery']
      };
      return typeMapping[type] || ['restaurant'];
    };

    const requestBody = {
      includedTypes: getIncludedTypes(placeType),
      maxResultCount: 10,
      locationRestriction: {
        circle: {
          center: {
            latitude: latitude,
            longitude: longitude
          },
          radius: searchRadius
        }
      },
      languageCode: 'en'
    };

    if (window.adminDebug) {
      window.adminDebug.addLog('Searching nearby places with new API', 'api', requestBody);
    }

    try {
      const response = await fetch('https://places.googleapis.com/v1/places:searchNearby', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': process.env.REACT_APP_GOOGLE_API_KEY,
          'X-Goog-FieldMask': 'places.displayName,places.formattedAddress,places.location,places.rating,places.priceLevel,places.types,places.id'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Places API error: ${response.status} - ${errorData}`);
      }

      const data = await response.json();
      
      if (data.places && data.places.length > 0) {
        setNearbyPlaces(data.places);
        
        // Create new markers and store them for cleanup
        const newMarkers = [];
        data.places.forEach((place, index) => {
          const marker = new window.google.maps.Marker({
            position: {
              lat: place.location.latitude,
              lng: place.location.longitude
            },
            map: map,
            title: place.displayName?.text || 'Unknown Place',
            icon: {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 2C10.48 2 6 6.48 6 12C6 20 16 30 16 30S26 20 26 12C26 6.48 21.52 2 16 2Z" fill="#EA4335"/>
                  <circle cx="16" cy="12" r="4" fill="white"/>
                </svg>
              `),
              scaledSize: new window.google.maps.Size(32, 32),
            },
          });

          // Enhanced click listener for place details
          marker.addListener('click', () => {
            getPlaceDetails(place.id);
            // Center map on selected place
            map.panTo({
              lat: place.location.latitude,
              lng: place.location.longitude
            });
          });

          newMarkers.push(marker);
        });

        setMarkers(newMarkers);

        if (window.adminDebug) {
          window.adminDebug.addLog(`Found ${data.places.length} nearby places`, 'info', {
            searchType: placeType,
            radius: searchRadius,
            resultsCount: data.places.length
          });
        }
      } else {
        setNearbyPlaces([]);
        if (window.adminDebug) {
          window.adminDebug.addLog('No places found', 'info', { searchType: placeType, radius: searchRadius });
        }
      }
    } catch (error) {
      setError(`Places search failed: ${error.message}`);
      if (window.adminDebug) {
        window.adminDebug.addLog('Places search failed', 'error', { 
          error: error.message,
          searchType: placeType,
          radius: searchRadius
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Get detailed information about a place using Places API (New)
  const getPlaceDetails = async (placeId) => {
    try {
      const response = await fetch(`https://places.googleapis.com/v1/places/${placeId}`, {
        method: 'GET',
        headers: {
          'X-Goog-Api-Key': process.env.REACT_APP_GOOGLE_API_KEY,
          'X-Goog-FieldMask': 'displayName,formattedAddress,rating,nationalPhoneNumber,websiteUri,photos,currentOpeningHours,priceLevel'
        }
      });

      if (!response.ok) {
        throw new Error(`Place details error: ${response.status}`);
      }

      const place = await response.json();
      
      if (window.adminDebug) {
        window.adminDebug.addLog('Place details retrieved', 'api', {
          name: place.displayName?.text,
          rating: place.rating,
          address: place.formattedAddress
        });
      }
      
      alert(`Place: ${place.displayName?.text || 'Unknown'}\nRating: ${place.rating || 'N/A'}\nAddress: ${place.formattedAddress || 'N/A'}`);
    } catch (error) {
      if (window.adminDebug) {
        window.adminDebug.addLog('Place details failed', 'error', { error: error.message, placeId });
      }
    }
  };

  return (
    <div className="maps-test-page">
      <div className="maps-test-header">
        <h3>🗺️ Maps & Places API (New) Test</h3>
        <div className="test-status">
          {error && <span className="status-error">❌ {error}</span>}
          {isInitializing && !error && <span className="status-loading">⏳ Initializing Maps...</span>}
          {loading && <span className="status-loading">⏳ Loading...</span>}
          {!error && !loading && !isInitializing && map && <span className="status-success">✅ Maps loaded</span>}
        </div>
      </div>

      <div className="maps-test-controls">
        <div className="control-group">
          <label>Place Type:</label>
          <select value={placeType} onChange={(e) => setPlaceType(e.target.value)}>
            <option value="restaurant">Restaurants</option>
            <option value="cafe">Cafes</option>
            <option value="bar">Bars</option>
            <option value="food">Food Places</option>
            <option value="meal_takeaway">Takeaway</option>
            <option value="bakery">Bakeries</option>
          </select>
        </div>

        <div className="control-group">
          <label>Search Radius:</label>
          <select value={searchRadius} onChange={(e) => setSearchRadius(Number(e.target.value))}>
            <option value={500}>500m</option>
            <option value={1000}>1km</option>
            <option value={2000}>2km</option>
            <option value={5000}>5km</option>
          </select>
        </div>

        <button 
          onClick={searchNearbyPlaces} 
          disabled={!map || loading}
          className="search-button"
        >
          {loading ? 'Searching...' : 'Search Nearby Places'}
        </button>

        <button 
          onClick={() => setMapType(mapType === 'roadmap' ? 'satellite' : 'roadmap')}
          disabled={!map}
          className="map-type-button"
          title={`Switch to ${mapType === 'roadmap' ? 'satellite' : 'road'} view`}
        >
          {mapType === 'roadmap' ? '🛰️ Satellite' : '🗺️ Road Map'}
        </button>
      </div>

      <div className="maps-test-content">
        <div className="map-container">
          {isInitializing && !error && (
            <div className="map-loading">
              <div className="loading-spinner"></div>
              <p>Loading Google Maps...</p>
            </div>
          )}
          {error && (
            <div className="map-error">
              <div className="error-icon">❌</div>
              <p>{error}</p>
              <small>Check the API Status section below for more details</small>
            </div>
          )}
          <div ref={mapRef} className="map" style={{ display: (isInitializing || error) ? 'none' : 'block' }} />
        </div>

        <div className="places-list">
          <h4>Nearby Places ({nearbyPlaces.length})</h4>
          {nearbyPlaces.length === 0 && !loading && (
            <p className="no-results">No places found. Try searching!</p>
          )}
          {nearbyPlaces.map((place, index) => (
            <div key={place.id} className="place-item">
              <div className="place-info">
                <h5>{place.displayName?.text || 'Unknown Place'}</h5>
                <p className="place-address">{place.formattedAddress}</p>
                <div className="place-meta">
                  {place.rating && (
                    <span className="place-rating">⭐ {place.rating}</span>
                  )}
                  {place.priceLevel && (
                    <span className="place-price">
                      {'$'.repeat(place.priceLevel)}
                    </span>
                  )}
                  <span className="place-type">{place.types?.[0]?.replace(/_/g, ' ')}</span>
                </div>
              </div>
              <button 
                onClick={() => getPlaceDetails(place.id)}
                className="details-button"
              >
                Details
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="api-info">
        <h4>API Status</h4>
        <div className="api-status-grid">
          <div className="api-status-item">
            <span className="api-label">Google Maps API:</span>
            <span className={`api-value ${window.google ? 'success' : 'error'}`}>
              {window.google ? 'Loaded' : 'Not Loaded'}
            </span>
          </div>
          <div className="api-status-item">
            <span className="api-label">Places API (New):</span>
            <span className={`api-value ${process.env.REACT_APP_GOOGLE_API_KEY ? 'success' : 'error'}`}>
              {process.env.REACT_APP_GOOGLE_API_KEY ? 'Ready' : 'Not Ready'}
            </span>
          </div>
          <div className="api-status-item">
            <span className="api-label">User Location:</span>
            <span className={`api-value ${userLocation ? 'success' : 'warning'}`}>
              {userLocation ? 'Obtained' : 'Using Default'}
            </span>
          </div>
          <div className="api-status-item">
            <span className="api-label">API Key:</span>
            <span className={`api-value ${process.env.REACT_APP_GOOGLE_API_KEY ? 'success' : 'error'}`}>
              {process.env.REACT_APP_GOOGLE_API_KEY ? 'Configured' : 'Missing'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapsTestPage; 